import { StripDiv, DrodownList, DropdownBtn, ModalBox, ModalBoxWrap } from './style';
import { RootState } from 'store';
import {
	enableGutterLines,
	setCurrentSelectedLine,
	enableAddGutterLines,
	enableRemoveGutterLines,
	enableEdgeLines,
	updateRemoveAllGutterLine,
} from 'store/slices/RoofToolLinesSlice';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useRoofLines from 'components/tool/Facet/useRoofLines';
import { isSpecialFacetLine } from 'components/tool/Facet/utils';
import PromtModeStrip from './prompt';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
import {BulkDeleteIcon} from '../../../../../../assets/svg';
import { DESIGN_MODES } from '../../../../../../constants';

export default function EdgeToolStrip() {
	const roofLines = useRoofLines();

	const [showLinePrompt, setShowLinePrompt] = useState<{ visible: boolean; lineType: string | null }>({
		visible: false,
		lineType: null
	});

	const { fullScreenEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const {
		activeEdgeTool,
		activeGutterLine,
		addGutterLine,
		removeGutterLine,
		currentSelectedLine,
		createSpecialEdges,
		updateSpecialEdges,
		selectedFacets,
		effectedFacets
	} = useSelector((state: RootState) => state.roofToolLines.data);

	const { designType, mode, allGutterLines: allExistingGutterLines } = useSelector((state: RootState) => state.roofData.data);
	const showAutoEdgeDetectionButton = designType === 'ROOF' && mode !== DESIGN_MODES.NEARMAP_NREL_MANUAL;

	const [removeGutterConfirmation, setRemoveGutterConfirmation] = useState<boolean>(false);
	
	const tempUsermappedGutterLines = Object.values(selectedFacets).flatMap(edgedata => edgedata.newAddedGutterLines);
	const tempRemovedGutterLines = Object.values(selectedFacets).flatMap(edgedata => edgedata.removedGutterLineIds);
	
	const allGutterLines = useMemo(() => {
		let gutteLines: { facetId: string | null, id: string | number }[] = [];
		gutteLines = tempUsermappedGutterLines.map(gl => ({ facetId: gl.facetId, id: gl.id }));
		gutteLines = gutteLines.concat(
			allExistingGutterLines.map(gl => ({ facetId: gl.roofSegmentId, id: gl.id }))
		);
		return gutteLines;
	}, [allExistingGutterLines, tempUsermappedGutterLines]);
	
	const disableRemoveAllBtn = !tempUsermappedGutterLines?.length
			&& allExistingGutterLines.length === tempRemovedGutterLines?.length;

	const needDisableRoofToolLines = useMemo(() => {
		return effectedFacets.length > 0;
	}, [effectedFacets]);

	const dispatch = useDispatch();
	const handleSelect = (lineType: string) => {
		if (needDisableRoofToolLines) {
			return;
		}
		// setOpen(false);
		dispatch(setCurrentSelectedLine({ lineType: lineType }));
		if (isSpecialFacetLine(lineType)) {
			setShowLinePrompt({ visible: true, lineType: lineType.slice(0, lineType.length - 1) ?? '' });
		} else {
			setShowLinePrompt({ visible: false, lineType: null });
		}
	};
	const promptClick = () => {
		setShowLinePrompt({ visible: false, lineType: null });
	};

	const toggleDropdown = () => {
		// setOpen(!open);
		if (needDisableRoofToolLines) {
			return;
		}
		dispatch(enableEdgeLines({ shouldEnable: !activeEdgeTool }));
	};
	const toggleDropdownGuiter = () => {
		if (needDisableRoofToolLines) {
			return;
		}
		if (!activeGutterLine) {
			setShowLinePrompt({ visible: true, lineType: 'gutter line' });
		} else {
			if (isSpecialFacetLine(currentSelectedLine)) {
				setShowLinePrompt({ visible: true, lineType: currentSelectedLine?.slice(0, currentSelectedLine?.length - 1) ?? '' });
			} else {
				setShowLinePrompt({ visible: false, lineType: null });
			}
		}
		dispatch(enableGutterLines({ shouldEnable: !activeGutterLine }));
	};
	return (
		<>
			<ModalBoxWrap className={`${fullScreenEnabled ? 'fullscreen edgetool-modalbox' : 'edgetool-modalbox'} ${showAutoEdgeDetectionButton ? 'with-auto-detect' : ''}`}>
				<ModalBox>

					<StripDiv 
						className={` ${activeEdgeTool ? 'active-strip' : ''}`}
					>
						<DropdownBtn onClick={toggleDropdown} 
							className={` ${activeEdgeTool ? 'arrow-icon ' : 'arrow-icon active'} ${needDisableRoofToolLines ? 'not-allowed' : ''}`} 
						>
							<label>
								<svg xmlns="http://www.w3.org/2000/svg" width="22" height="17.3" viewBox="0 0 47 37" fill="none">
									<path d="M44.2131 2.26758H2V34.637H44.2131C44.9378 34.637 44.5151 13.0574 44.2131 2.26758Z"
										stroke={`${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinejoin="round" />
									<path d="M44.2095 34.637L32.3125 18.6335L44.2095 2.26758"
										stroke={`${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
									<path d="M13.9648 18.8184H32.2632" stroke={`${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--defaultIconColor)'}`}
										strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
									<path d="M2.24755 34.637L14.1445 18.6335L2.24755 2.26758"
										stroke={`${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--defaultIconColor)'}`} strokeWidth="3.02972" strokeLinecap="round" strokeDasharray="2.21 4.42" />
								</svg>
								<span style={{ color: ` ${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--subHeadingColor)'}` }}>Edit Edges</span>
							</label>
							<span
								className={` ${!activeEdgeTool ? 'active arrow-down' : ' arrow-down'}`}>
								<svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
									<path opacity="0.8" d="M4 5C4 5 1.45455 1.66667 0 0L8 0C6.54545 1.66667 4 5 4 5Z" fill={`${!activeEdgeTool ? 'var(--btnLabel)' : 'var(--defaultIconColor)'}`} />
								</svg>
							</span>
						</DropdownBtn>
						{!activeEdgeTool && (
							<DrodownList
								className={` ${needDisableRoofToolLines ? 'not-allowed' : ''} dropdown-list`}
							>
								{Object.keys(roofLines).map((currentLineKey, index) => {
									const currentLine = roofLines[currentLineKey];
									const isSelected = currentLineKey === currentSelectedLine;
									return (
										<li
											key={index}
											onClick={() => handleSelect(currentLineKey)}
											className={isSelected ? 'selected' : ''}
											style={{
												background: `${isSelected ? 'rgb(from var(--primary) r g b / 10%)' : 'var(--selectDropdownBgColor)'}`,
												// borderLeft: `4px ${item.dashed ? 'dashed' : 'solid'} ${item.color}`,
											}}
										>
											<span style={{ borderTop: `4px ${currentLine.dashed.length > 0 ? 'dashed' : 'solid'} ${currentLine.color}`, }}></span>
											{currentLine.name}
										</li>
									);
								})}
							</DrodownList>
						)}
					</StripDiv>
					<StripDiv className={` ${activeGutterLine ? 'active-strip' : ''}`}>

						<DropdownBtn 
							onClick={toggleDropdownGuiter} 
							className={` ${activeGutterLine ? 'arrow-icon active' : 'arrow-icon'}  ${needDisableRoofToolLines ? 'not-allowed' : ''}`} 
						>
							<label>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M1.5 14.5L15 1" stroke={`${activeGutterLine ? 'var(--btnLabel)' : '#FF8E25'}`} strokeWidth="1.5" />
									<rect x="12.5" y="0.5" width="3" height="3" fill={`${activeGutterLine ? 'var(--btnLabel)' : '#FF8E25'}`} stroke={`${activeGutterLine ? 'var(--btnLabel)' : '#FF8E25'}`} />
									<rect x="1.5" y="11.5" width="3" height="3" fill={`${activeGutterLine ? 'var(--btnLabel)' : '#FF8E25'}`} stroke={`${activeGutterLine ? 'var(--btnLabel)' : '#FF8E25'}`} />
								</svg>
								<span style={{ color: ` ${activeGutterLine ? 'var(--btnLabel)' : 'var(--subHeadingColor)'} ` }}>Gutter line</span>
							</label>
							<span
								className={` ${activeGutterLine ? 'active' : ''}`}>
								<svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
									<path opacity="0.8" d="M4 5C4 5 1.45455 1.66667 0 0L8 0C6.54545 1.66667 4 5 4 5Z" fill={`${activeGutterLine ? '#fff' : 'var(--defaultIconColor)'}`} />
								</svg>
							</span>
						</DropdownBtn>
						{activeGutterLine && (
							<DrodownList
								className='dropdown-list gutter-list'
								style={{

								}}
							>
								<li onClick={() => {
									if (needDisableRoofToolLines) {
										return;
									}
									dispatch(enableAddGutterLines({ shouldEnable: true }));
								}}>
									<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M1.5 14.5L15 1" stroke={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.5" />
										<rect x="12.5" y="0.5" width="3" height="3" fill={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} stroke={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} />
										<rect x="1.5" y="11.5" width="3" height="3" fill={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} stroke={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} />
										<path d="M13 9V15" stroke={`${addGutterLine ? 'var(--primary)' : '#FF53EE'}`} strokeWidth="1.3" />
										<path d="M13 9V15" stroke={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.3" />
										<path d="M16.0039 11.9941L10.0039 11.9941" stroke={`${addGutterLine ? 'var(--primary)' : '#FF53EE'}`} strokeWidth="1.3" />
										<path d="M16.0039 11.9941L10.0039 11.9941" stroke={`${addGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.3" />
									</svg>
									<span style={{ color: `${addGutterLine ? 'var(--primary)' : 'var(--textColor)'}` }}>Add</span>

								</li>
								<li onClick={() => {
									if (needDisableRoofToolLines) {
										return;
									}
									dispatch(enableRemoveGutterLines({ shouldEnable: true }));
								}}
								style={{
									cursor: needDisableRoofToolLines ? 'not-allowed' : 'pointer'
								}}
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
										<path d="M1.5 14.5L15 1" stroke={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.5" />
										<rect x="12.5" y="0.5" width="3" height="3" fill={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} stroke={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} />
										<rect x="1.5" y="11.5" width="3" height="3" fill={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} stroke={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} />
										<path d="M16.0039 11.9941L10.0039 11.9941" stroke={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.3" />
										<path d="M16.0039 11.9941L10.0039 11.9941" stroke={`${removeGutterLine ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.3" />
									</svg>
									<span style={{ color: `${removeGutterLine ? 'var(--primary)' : 'var(--textColor)'}` }}>Remove</span>
								</li>

								<li onClick={() => {
									if (needDisableRoofToolLines || disableRemoveAllBtn) {
										return;
									}
									setRemoveGutterConfirmation(true);
								}}
								style={{
									cursor: (needDisableRoofToolLines || disableRemoveAllBtn) ? 'not-allowed' : 'pointer'
								}}
								>
									<BulkDeleteIcon/>
									<span style={{ color: 'var(--textColor)' }}>Remove All</span>
								</li>
							</DrodownList>
						)}
					</StripDiv>
				</ModalBox>
			</ModalBoxWrap>

			{effectedFacets.length > 0 && <PromtModeStrip />}

			<ConfirmationModal
				open={removeGutterConfirmation}
				message='Are you sure you want to remove all gutter lines?'
				onCancel={() => setRemoveGutterConfirmation(false)}
				onConfirm={()=>{
					dispatch(updateRemoveAllGutterLine(allGutterLines));
					setRemoveGutterConfirmation(false);
				}}
			/>
		</>
	);
}